import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus, FaEdit, FaTrash, FaImage } from "react-icons/fa";
import { blogService, BlogPost, mediaService } from "../../services/firebase";
import "./Blog.css";
import PrimaryButton from "components/PrimaryButton";
import RefresIcon from "assets/icone/RefresIcon";

const AdminBlog: React.FC = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    titleRu: "",
    content: "",
    contentRu: "",
    imageUrl: "",
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const editorRuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  // Update editor content when form data changes
  useEffect(() => {
    if (editorRef.current && isModalOpen) {
      const currentContent = editorRef.current.innerHTML;
      const newContent =
        formData.content ||
        "<p>הכנס תוכן הפוסט... (תוכל להשתמש בכפתורים למעלה להדגשה ורשימות)</p>";

      if (currentContent !== newContent) {
        editorRef.current.innerHTML = newContent;
      }
    }
  }, [formData.content, isModalOpen]);

  // Update Russian editor content when form data changes
  useEffect(() => {
    if (editorRuRef.current && isModalOpen) {
      const currentContent = editorRuRef.current.innerHTML;
      const newContent =
        formData.contentRu ||
        "<p>Введите содержимое поста... (вы можете использовать кнопки выше для выделения и списков)</p>";

      if (currentContent !== newContent) {
        editorRuRef.current.innerHTML = newContent;
      }
    }
  }, [formData.contentRu, isModalOpen]);

  const fetchPosts = async () => {
    try {
      const data = (await blogService.getAll()) as BlogPost[];
      setPosts(data);
    } catch (error) {
          }
  };

  const handleAddClick = () => {
    setEditingPost(null);
    setFormData({
      title: "",
      titleRu: "",
      content: "",
      contentRu: "",
      imageUrl: "",
    });
    clearImageSelection();
    setIsModalOpen(true);
  };

  const handleEditClick = (post: BlogPost) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      titleRu: post.titleRu || "",
      content: post.content,
      contentRu: post.contentRu || "",
      imageUrl: post.imageUrl || "",
    });
    // Set image preview if exists
    if (post.imageUrl) {
      setImagePreview(post.imageUrl);
    } else {
      setImagePreview("");
    }
    // Clear only selectedFile, not imagePreview
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleDeleteClick = async (id: string) => {
    if (window.confirm(t("admin.confirmDelete"))) {
      try {
        await blogService.delete(id);
        fetchPosts();
      } catch (error) {
              }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // אם משנים את ה-URL של התמונה, עדכן את התצוגה המקדימה
    if (name === "imageUrl") {
      setImagePreview(value);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);

      // יצירת תצוגה מקדימה של התמונה
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async (): Promise<string> => {
    if (!selectedFile) return formData.imageUrl;

    try {
      setIsUploading(true);
      const uploadedImage = await mediaService.upload(selectedFile, "blog");
      return uploadedImage.url;
    } catch (error) {
            throw new Error("שגיאה בהעלאת התמונה");
    } finally {
      setIsUploading(false);
    }
  };

  const clearImageSelection = () => {
    setSelectedFile(null);
    setImagePreview("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Upload image if selected
      let finalImageUrl = formData.imageUrl;
      if (selectedFile) {
        finalImageUrl = await handleImageUpload();
      }

      const postData = {
        ...formData,
        imageUrl: finalImageUrl,
      };

      if (editingPost && editingPost.id) {
        await blogService.update(editingPost.id, postData);
      } else {
        await blogService.create(postData);
      }

      // Reset form and image
      setFormData({
        title: "",
        titleRu: "",
        content: "",
        contentRu: "",
        imageUrl: "",
      });
      clearImageSelection();
      setIsModalOpen(false);
      fetchPosts();
    } catch (error) {
            alert(error instanceof Error ? error.message : "שגיאה בשמירת הפוסט");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="page-blog-container-admin">
      <div className="header-blog-admin">
        <h1 className="title">{t("admin.blog")}</h1>
        <PrimaryButton
          className="add-button-admin"
          icon={<FaPlus />}
          onClick={handleAddClick}
        >
          {t("admin.addPost")}
        </PrimaryButton>
      </div>

      <div className="blog-grid-admin">
        {posts.map((post) => (
          <div className="blog-card-admin" key={post.id}>
            <img
              className="blog-image-admin"
              src={post.imageUrl || "/images/blog-default.jpg"}
              alt={post.title}
            />
            <div className="blog-content-admin">
              <h3 className="blog-title-admin">{post.title}</h3>

              <div className="blog-actions-admin">
                <PrimaryButton
                  icon={<FaEdit />}
                  className="action-button-edit-admin"
                  onClick={() => handleEditClick(post)}
                >
                  ערוך פוסט
                </PrimaryButton>
                <PrimaryButton
                  className="action-button-delete-admin"
                  icon={<FaTrash />}
                  style={{ backgroundColor: "red" }}
                  onClick={() => post.id && handleDeleteClick(post.id)}
                >
                  {" "}
                  מחק פוסט
                </PrimaryButton>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className="modal-admin-blog">
          <div className="modal-content-admin-blog">
            <h2>{editingPost ? t("admin.editPost") : t("admin.addPost")}</h2>
            <form className="form-admin-blog" onSubmit={handleSubmit}>
              <div className="form-group-admin-blog">
                <label className="label-admin-blog">כותרת HE</label>
                <input
                  className="input-admin-blog"
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group-admin-blog">
                <label className="label-admin-blog">כותרת RU</label>
                <input
                  className="input-admin-blog"
                  type="text"
                  name="titleRu"
                  value={formData.titleRu}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group-admin-blog">
                <label className="label-admin-blog">תוכן HE</label>
                <div className="wysiwyg-editor-container-admin-blog">
                  <div className="wysiwyg-toolbar-admin-blog">
                    <button
                      type="button"
                      className="toolbar-button-admin-blog"
                      onClick={() => {
                        document.execCommand("bold", false);
                      }}
                      title="הדגש טקסט"
                    >
                      <strong>Bold</strong>
                    </button>
                    <button
                      type="button"
                      className="toolbar-button-admin-blog"
                      onClick={() => {
                        document.execCommand("insertUnorderedList", false);
                      }}
                      title="הוסף רשימה עם נקודות"
                    >
                      • רשימה
                    </button>
                  </div>
                  <div
                    ref={editorRef}
                    className="wysiwyg-editor-admin-blog"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onInput={(e) => {
                      const html = e.currentTarget.innerHTML;
                      setFormData({ ...formData, content: html });
                    }}
                  />
                </div>
              </div>
              <div className="form-group-admin-blog">
                <label className="label-admin-blog">תוכן RU</label>
                <div className="wysiwyg-editor-container">
                  <div className="wysiwyg-toolbar-admin-blog">
                    <button
                      type="button"
                      className="toolbar-button-admin-blog"
                      onClick={() => {
                        document.execCommand("bold", false);
                      }}
                      title="הדגש טקסט"
                    >
                      <strong>Bold</strong>
                    </button>
                    <button
                      type="button"
                      className="toolbar-button-admin-blog"
                      onClick={() => {
                        document.execCommand("insertUnorderedList", false);
                      }}
                      title="הוסף רשימה עם נקודות"
                    >
                      • רשימה
                    </button>
                  </div>
                  <div
                    ref={editorRuRef}
                    className="wysiwyg-editor-admin-blog"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onInput={(e) => {
                      const html = e.currentTarget.innerHTML;
                      setFormData({ ...formData, contentRu: html });
                    }}
                  />
                </div>
              </div>
              <div className="form-group-admin-blog">
                <label className="label-admin-blog">תמונה</label>
                <div className="image-upload-container-admin-blog">
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="image-preview-admin-blog">
                      <img src={imagePreview} alt="תמונה נבחרת" />
                      <button
                        type="button"
                        className="remove-image-btn-admin-blog"
                        onClick={clearImageSelection}
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* File Input */}
                  <div className="file-input-container">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="file-input"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="file-input-label">
                      {selectedFile || imagePreview ? (
                        <RefresIcon />
                      ) : (
                        <FaPlus />
                      )}
                      {selectedFile || imagePreview
                        ? "שנה תמונה"
                        : "העלאת תמונה"}
                    </label>
                  </div>

                  {/* Upload Progress */}
                  {isUploading && (
                    <div className="upload-progress">
                      <FaImage className="upload-icon" />
                      מעלה תמונה...
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-actions-admin-blog">
                <button
                  type="submit"
                  className="button-admin-blog primary"
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <div className="loading-spinner-admin-blog" />
                      {t("admin.uploading") || "מעלה..."}
                    </>
                  ) : editingPost ? (
                    t("admin.saveChanges")
                  ) : (
                    t("admin.addPost")
                  )}
                </button>
                <button
                  type="button"
                  className="button-admin-blog secondary"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isUploading}
                >
                  {t("admin.cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
